import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { collection, doc, getCountFromServer, getDoc, getDocs, limit, query, startAfter, where } from "firebase/firestore";
import { firestoreDev, firestoreProd } from "../../firebase";
import {
  FaBuilding,
  FaDatabase,
  FaExternalLinkAlt,
  FaFileAlt,
  FaSearch,
  FaSpinner,
  FaTimes,
  FaWindowRestore,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDarkMode } from "../../components/Theme/useDarkMode";

const PAGE_SIZE = 500;
const ORG_RESULT_LIMIT = 20;

const tokenise = (value) =>
  String(value || "")
    .toLowerCase()
    .split(/[\s.\-_@,/]+/)
    .filter(Boolean);

const scoreDoc = (fields, term) => {
  if (!term) return 1;

  const queryText = term.toLowerCase().trim();
  const queryTokens = tokenise(queryText);
  let best = 0;

  for (const rawField of fields) {
    if (rawField == null) continue;

    const field = String(rawField).toLowerCase();

    if (field === queryText) {
      best = Math.max(best, 3);
      continue;
    }

    if (field.startsWith(queryText)) {
      best = Math.max(best, 2);
      continue;
    }

    if (queryTokens.length > 0 && queryTokens.every((token) => field.includes(token))) {
      best = Math.max(best, 1);
    }
  }

  return best;
};

const getOrgLabel = (org) =>
  String(
    org?.name ||
      org?.organizationName ||
      org?.companyName ||
      org?.orgName ||
      org?.id ||
      "Unknown organization"
  );

const normalizeOrganization = (id, data = {}) => ({
  id,
  name: getOrgLabel({ id, ...data }),
  defaultForUserId: String(data.defaultForUserId || ""),
});

const getLoanNumber = (loan) => loan.number || loan.loanNumber || null;

const getLoanLabel = (loan) => getLoanNumber(loan) || loan.name || loan.id || "Untitled loan";

const getBorrowerName = (loan) => {
  const borrower = loan.borrowers?.[0];

  if (borrower) {
    const borrowerName = [borrower.firstName, borrower.lastName].filter(Boolean).join(" ").trim();
    return borrowerName || borrower.name || borrower.email || borrower.phone || null;
  }

  return (
    loan.borrowerName ||
    loan.borrowerFullName ||
    loan.primaryBorrowerName ||
    loan.borrower ||
    null
  );
};

const getPropertyAddress = (loan) =>
  loan.address ||
  loan.propertyAddress ||
  loan.subjectPropertyAddress ||
  loan.property?.address ||
  null;

const getLoanStage = (loan) =>
  loan.stage ||
  loan.loanStage ||
  loan.boardColumn ||
  loan.boardColumnName ||
  loan.status ||
  null;

const getLoanType = (loan) =>
  loan.loanType ||
  loan.type ||
  loan.purpose ||
  loan.loanPurpose ||
  null;

const getUpdatedAtMs = (loan) => {
  const raw =
    loan.updatedAt ||
    loan.updated_at ||
    loan.lastModified ||
    loan.lastUpdated ||
    null;

  if (!raw) return 0;
  try {
    const date = typeof raw.toDate === "function" ? raw.toDate() : new Date(raw);
    return isNaN(date.getTime()) ? 0 : date.getTime();
  } catch {
    return 0;
  }
};

const formatUpdatedAt = (loan) => {
  const raw =
    loan.updatedAt ||
    loan.updated_at ||
    loan.lastModified ||
    loan.lastUpdated ||
    null;

  if (!raw) return null;

  try {
    const date = typeof raw.toDate === "function" ? raw.toDate() : new Date(raw);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return null;
  }
};

const collectSearchValues = (input, depth = 0) => {
  if (input == null || depth > 3) return [];

  if (typeof input === "string" || typeof input === "number" || typeof input === "boolean") {
    return [String(input)];
  }

  if (Array.isArray(input)) {
    return input.flatMap((item) => collectSearchValues(item, depth + 1));
  }

  if (typeof input === "object") {
    return Object.values(input).flatMap((value) => collectSearchValues(value, depth + 1));
  }

  return [];
};

const loanThinFields = (loan) => {
  const commonFields = [
    loan.id,
    getLoanNumber(loan),
    loan.name,
    loan.externalId,
    loan.losId,
    loan.organizationId,
    getLoanStage(loan),
    getBorrowerName(loan),
    getPropertyAddress(loan),
    loan.city,
    loan.state,
    loan.zipCode,
    loan.authorName,
    loan.authorEmail,
    loan.loanOfficerName,
    loan.loanOfficerEmail,
    loan.loanType,
    loan.purpose,
    loan.amount,
    loan.loanAmount,
  ];

  return [...commonFields, ...collectSearchValues(loan)];
};

const normalizeStageOptions = (loanBoardColumns) => {
  if (!Array.isArray(loanBoardColumns)) return [];

  const options = loanBoardColumns
    .map((column) => {
      if (typeof column === "string") return column.trim();
      if (!column || typeof column !== "object") return "";

      return String(
        column.title ||
          column.name ||
          column.label ||
          column.value ||
          column.id ||
          ""
      ).trim();
    })
    .filter(Boolean);

  return [...new Set(options)];
};

const normalizeLoanSnapshot = (snapshot, organizationId) => ({
  id: snapshot.id,
  organizationId,
  ...snapshot.data(),
});

const dedupeLoansById = (loans) => Array.from(new Map(loans.map((loan) => [loan.id, loan])).values());

const searchOrganizationOptions = (organizations, searchTerm) => {
  const normalizedSearch = searchTerm.trim().toLowerCase();

  if (!normalizedSearch) {
    return organizations.slice(0, ORG_RESULT_LIMIT);
  }

  return organizations
    .filter((org) => {
      const name = getOrgLabel(org).toLowerCase();
      const ownerId = String(org.defaultForUserId || "").toLowerCase();
      const id = org.id.toLowerCase();

      return (
        name.includes(normalizedSearch) ||
        ownerId.includes(normalizedSearch) ||
        id.includes(normalizedSearch)
      );
    })
    .slice(0, ORG_RESULT_LIMIT);
};

const HighlightText = ({ text, term }) => {
  if (!text) return <span>—</span>;
  if (!term) return <span>{text}</span>;

  try {
    const tokens = tokenise(term);

    if (!tokens.length) {
      return <span>{text}</span>;
    }

    const pattern = tokens
      .map((token) => token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
      .join("|");
    const regex = new RegExp(`(${pattern})`, "gi");
    const parts = String(text).split(regex);

    return (
      <span>
        {parts.map((part, index) =>
          regex.test(part) ? (
            <mark
              key={index}
              style={{
                background: "#7F7FD5",
                color: "#fff",
                borderRadius: "2px",
                padding: "0 2px",
              }}
            >
              {part}
            </mark>
          ) : (
            <span key={index}>{part}</span>
          )
        )}
      </span>
    );
  } catch {
    return <span>{text}</span>;
  }
};

const OrganizationLoanSearchPage = ({ onReady }) => {
  const [theme] = useDarkMode();
  const isDark = theme === "dark";

  const history = useHistory();
  const location = useLocation();

  const orgIdFromUrl = useMemo(
    () => new URLSearchParams(location.search).get("org") || null,
    [location.search]
  );

  const [environment, setEnvironment] = useState("prod");
  const [organizationCache, setOrganizationCache] = useState([]);
  const [organizationSearchQuery, setOrganizationSearchQuery] = useState("");
  const [organizationResults, setOrganizationResults] = useState([]);
  const [isSearchingOrganizations, setIsSearchingOrganizations] = useState(false);
  const [orgScanProgress, setOrgScanProgress] = useState({ scanned: 0, total: null });
  const [stageOptions, setStageOptions] = useState([]);
  const [stageFilter, setStageFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const [results, setResults] = useState([]);
  const [isSearchingLoans, setIsSearchingLoans] = useState(false);
  const [scanProgress, setScanProgress] = useState({ scanned: 0, total: null });
  const [hasSearched, setHasSearched] = useState(false);
  const [hasLoadedOrganizations, setHasLoadedOrganizations] = useState(false);
  const inputRef = useRef(null);
  const loanListenerRef = useRef(null);
  const orgSearchRef = useRef(null);
  const isMountedRef = useRef(false);

  const firestore = useMemo(
    () => (environment === "prod" ? firestoreProd : firestoreDev),
    [environment]
  );

  const selectedOrganization = useMemo(
    () => (orgIdFromUrl ? (organizationCache.find((o) => o.id === orgIdFromUrl) ?? null) : null),
    [orgIdFromUrl, organizationCache]
  );

  const cardPanelStyle = {
    background: isDark ? "#161b2e" : "#fff",
    border: `1px solid ${isDark ? "#2d3148" : "#e2e8f0"}`,
    borderRadius: "12px",
    padding: "24px",
    marginBottom: "24px",
    boxShadow: isDark ? "0 4px 24px rgba(0,0,0,0.3)" : "0 2px 12px rgba(0,0,0,0.06)",
  };

  const inputStyle = {
    flex: 1,
    padding: "12px 16px",
    fontSize: "15px",
    border: `1.5px solid ${isDark ? "#3d4466" : "#cbd5e0"}`,
    borderRadius: "8px",
    background: isDark ? "#1e2130" : "#f7f9fc",
    color: isDark ? "#e2e8f0" : "#1a202c",
    outline: "none",
    transition: "border-color 0.15s",
  };

  const searchButtonStyle = {
    padding: "12px 24px",
    fontSize: "14px",
    fontWeight: 600,
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    background: "#7F7FD5",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    whiteSpace: "nowrap",
  };

  const envToggleStyle = (env) => ({
    padding: "8px 16px",
    fontSize: "13px",
    fontWeight: 600,
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    background: environment === env ? "#7F7FD5" : isDark ? "#2d3148" : "#e2e8f0",
    color: environment === env ? "#fff" : isDark ? "#a0aec0" : "#4a5568",
    transition: "all 0.15s",
  });

  const loadOrganizations = useCallback(async () => {
    if (!firestore) {
      toast.error("Firebase is not initialized");
      return [];
    }

    const organizationSnapshot = await getDocs(
      query(collection(firestore, "organizations"), limit(1000))
    );
    const organizations = organizationSnapshot.docs
      .map((snapshot) => normalizeOrganization(snapshot.id, snapshot.data()))
      .sort((a, b) => getOrgLabel(a).localeCompare(getOrgLabel(b)));

    setOrganizationCache(organizations);
    setHasLoadedOrganizations(true);

    return organizations;
  }, [firestore]);

  const loadOrganizationStageOptions = useCallback(
    async (organizationId) => {
      if (!firestore || !organizationId) {
        setStageOptions([]);
        return [];
      }

      const organizationSnapshot = await getDoc(doc(firestore, "organizations", organizationId));

      if (!organizationSnapshot.exists()) {
        setStageOptions([]);
        return [];
      }

      const options = normalizeStageOptions(organizationSnapshot.data()?.loanBoardColumns);
      setStageOptions(options);
      return options;
    },
    [firestore]
  );

  useEffect(() => {
    onReady?.();
  }, [onReady]);

  useEffect(() => {
    let cancelled = false;

    const preloadOrganizations = async () => {
      try {
        const organizations = await loadOrganizations();

        if (!cancelled) {
          setOrganizationResults(searchOrganizationOptions(organizations, ""));
        }
      } catch (error) {
        console.error("Failed to preload organizations:", error);
        if (!cancelled) {
          toast.error("Failed to load organizations");
        }
      }
    };

    preloadOrganizations();

    return () => {
      cancelled = true;
    };
  }, [loadOrganizations]);

  useEffect(() => {
    return () => {
      loanListenerRef.current = null;
    };
  }, []);

  useEffect(() => {
    setStageOptions([]);
    setStageFilter("");
    setResults([]);
    setHasSearched(false);
    setSearchTerm("");
    setActiveSearchTerm("");

    if (!orgIdFromUrl || !organizationCache.length) return;

    const org = organizationCache.find((o) => o.id === orgIdFromUrl);
    if (!org) return;

    loadOrganizationStageOptions(org.id).catch(() => {});
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [orgIdFromUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the cache loads after the URL already has an org (e.g. direct link / page refresh)
  useEffect(() => {
    if (!orgIdFromUrl || !organizationCache.length) return;
    const org = organizationCache.find((o) => o.id === orgIdFromUrl);
    if (org) {
      loadOrganizationStageOptions(org.id).catch(() => {});
    }
  }, [organizationCache]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    const params = new URLSearchParams(location.search);
    params.delete("org");
    history.replace({ search: params.toString() });
    setStageOptions([]);
    setStageFilter("");
    setSearchTerm("");
    setActiveSearchTerm("");
    setResults([]);
    setHasSearched(false);
    setOrganizationSearchQuery("");
    setOrganizationResults(searchOrganizationOptions(organizationCache, ""));
  }, [environment]); // eslint-disable-line react-hooks/exhaustive-deps

  const runOrganizationSearch = useCallback(async () => {
    if (!firestore) {
      toast.error("Firebase is not initialized");
      return;
    }

    const searchId = Date.now();
    orgSearchRef.current = searchId;

    setIsSearchingOrganizations(true);
    setOrganizationResults([]);
    setOrgScanProgress({ scanned: 0, total: null });

    const trimmedSearch = organizationSearchQuery.trim();
    const normalizedSearch = trimmedSearch.toLowerCase();
    const orgsCollection = collection(firestore, "organizations");

    const matchOrg = (org) => {
      if (!normalizedSearch) return true;
      const name = getOrgLabel(org).toLowerCase();
      const ownerId = String(org.defaultForUserId || "").toLowerCase();
      const id = org.id.toLowerCase();
      return name.includes(normalizedSearch) || ownerId.includes(normalizedSearch) || id.includes(normalizedSearch);
    };

    try {
      if (trimmedSearch.length >= 10 && !trimmedSearch.includes(" ")) {
        const directSnapshot = await getDoc(doc(firestore, "organizations", trimmedSearch)).catch(() => null);
        if (directSnapshot?.exists()) {
          setOrganizationResults([normalizeOrganization(directSnapshot.id, directSnapshot.data())]);
          return;
        }
      }

      const activeOrgsQuery = query(orgsCollection, where("active", "==", true));

      getCountFromServer(activeOrgsQuery)
        .then((snap) => setOrgScanProgress((prev) => ({ ...prev, total: snap.data().count })))
        .catch(() => {});

      let accumulated = [];
      let cursor = null;
      let scanned = 0;

      while (orgSearchRef.current === searchId) {
        const pageQuery = cursor
          ? query(orgsCollection, where("active", "==", true), startAfter(cursor), limit(PAGE_SIZE))
          : query(orgsCollection, where("active", "==", true), limit(PAGE_SIZE));

        const snapshot = await getDocs(pageQuery);

        if (orgSearchRef.current !== searchId) break;

        scanned += snapshot.docs.length;
        const currentScanned = scanned;
        setOrgScanProgress((prev) => ({ ...prev, scanned: currentScanned }));

        const pageOrgs = snapshot.docs.map((s) => normalizeOrganization(s.id, s.data()));
        const pageMatches = pageOrgs.filter(matchOrg);

        if (pageMatches.length > 0) {
          accumulated = [...accumulated, ...pageMatches].sort((a, b) =>
            getOrgLabel(a).localeCompare(getOrgLabel(b))
          );
          setOrganizationResults([...accumulated]);
        }

        if (snapshot.docs.length < PAGE_SIZE) break;

        cursor = snapshot.docs[snapshot.docs.length - 1];
      }

      if (orgSearchRef.current === searchId) {
        setOrganizationCache(
          [...accumulated].sort((a, b) => getOrgLabel(a).localeCompare(getOrgLabel(b)))
        );
        setHasLoadedOrganizations(true);
      }
    } catch (error) {
      console.error("Failed to search organizations:", error);
      toast.error("Failed to search organizations");
    } finally {
      if (orgSearchRef.current === searchId) {
        setIsSearchingOrganizations(false);
      }
    }
  }, [firestore, organizationSearchQuery]);

  const selectOrganization = useCallback(
    (organization) => {
      const params = new URLSearchParams(location.search);
      params.set("org", organization.id);
      history.push({ search: params.toString() });
    },
    [history, location.search]
  );

  const clearSelectedOrganization = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete("org");
    history.push({ search: params.toString() });
    setStageOptions([]);
    setOrganizationResults(searchOrganizationOptions(organizationCache, organizationSearchQuery));
  }, [history, location.search, organizationCache, organizationSearchQuery]);

  const runLoanSearch = useCallback(async () => {
    if (!firestore || !selectedOrganization?.id) {
      toast.info("Select an organization before searching loans.");
      return;
    }

    const trimmedSearch = searchTerm.trim();

    if (!trimmedSearch && !stageFilter) {
      toast.info("Enter a search term or choose a stage.");
      return;
    }

    const searchId = Date.now();
    loanListenerRef.current = searchId;

    setIsSearchingLoans(true);
    setHasSearched(true);
    setActiveSearchTerm(trimmedSearch);
    setResults([]);
    setScanProgress({ scanned: 0, total: null });

    const loansCollection = collection(
      firestore,
      "organizations",
      selectedOrganization.id,
      "loans-thin"
    );

    const orgId = selectedOrganization.id;
    const stageFilterLower = stageFilter.toLowerCase();

    const matchLoan = (loan) => {
      const stageOk = !stageFilter || getLoanStage(loan)?.toLowerCase() === stageFilterLower;
      const score = scoreDoc(loanThinFields(loan), trimmedSearch);
      return stageOk && score > 0 ? { ...loan, _score: score } : null;
    };

    try {
      let exactMatches = [];

      if (trimmedSearch) {
        const [directLoanSnapshot, numberMatches, loanNumberMatches, losIdMatches] =
          await Promise.all([
            getDoc(doc(loansCollection, trimmedSearch)).catch(() => null),
            getDocs(query(loansCollection, where("number", "==", trimmedSearch), limit(10))),
            getDocs(query(loansCollection, where("loanNumber", "==", trimmedSearch), limit(10))),
            getDocs(query(loansCollection, where("losId", "==", trimmedSearch), limit(10))),
          ]);

        exactMatches = dedupeLoansById(
          [
            directLoanSnapshot?.exists() ? normalizeLoanSnapshot(directLoanSnapshot, orgId) : null,
            ...(numberMatches?.docs || []).map((s) => normalizeLoanSnapshot(s, orgId)),
            ...(loanNumberMatches?.docs || []).map((s) => normalizeLoanSnapshot(s, orgId)),
            ...(losIdMatches?.docs || []).map((s) => normalizeLoanSnapshot(s, orgId)),
          ].filter(Boolean)
        );
      }

      const sortByRecency = (a, b) =>
        getUpdatedAtMs(b) - getUpdatedAtMs(a) || b._score - a._score;

      let accumulatedMatches = exactMatches
        .map(matchLoan)
        .filter(Boolean)
        .sort(sortByRecency);

      getCountFromServer(loansCollection)
        .then((snap) => setScanProgress((prev) => ({ ...prev, total: snap.data().count })))
        .catch(() => {});

      let cursor = null;
      let scanned = 0;

      while (loanListenerRef.current === searchId) {
        const pageQuery = cursor
          ? query(loansCollection, startAfter(cursor), limit(PAGE_SIZE))
          : query(loansCollection, limit(PAGE_SIZE));

        const snapshot = await getDocs(pageQuery);

        if (loanListenerRef.current !== searchId) break;

        scanned += snapshot.docs.length;
        const currentScanned = scanned;
        setScanProgress((prev) => ({ ...prev, scanned: currentScanned }));

        const pageLoans = snapshot.docs.map((s) => normalizeLoanSnapshot(s, orgId));
        const pageMatches = pageLoans.map(matchLoan).filter(Boolean);

        if (pageMatches.length > 0) {
          accumulatedMatches = dedupeLoansById([...accumulatedMatches, ...pageMatches]).sort(sortByRecency);
          setResults([...accumulatedMatches]);
        }

        if (snapshot.docs.length < PAGE_SIZE) break;

        cursor = snapshot.docs[snapshot.docs.length - 1];
      }

      if (loanListenerRef.current === searchId && accumulatedMatches.length === 0) {
        toast.info(`No loans found for "${trimmedSearch || stageFilter}"`);
      }
    } catch (error) {
      console.error("Loan search failed:", error);
      toast.error("Loan search failed. Please try again.");
    } finally {
      if (loanListenerRef.current === searchId) {
        setIsSearchingLoans(false);
      }
    }
  }, [firestore, searchTerm, selectedOrganization, stageFilter]);

  const handleLoanSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      runLoanSearch();
    }
  };

  const openAllInTabs = () => {
    const confirmed = window.confirm(
      `This will open ${results.length} loan${results.length !== 1 ? "s" : ""} in new tabs, one every second.\n\nContinue?`
    );
    if (!confirmed) return;

    // All windows must be opened synchronously within the user gesture to avoid
    // being blocked by the browser's popup blocker. We open blank tabs now and
    // navigate each one after a staggered delay.
    const tabs = results.map(() => window.open("about:blank", "_blank"));

    if (!tabs[0]) {
      toast.error("Your browser is blocking popups. Please allow popups for this site and try again.");
      return;
    }

    results.forEach((loan, i) => {
      const appUrl = `https://app.addy.so/loans?tab=board&loan=${loan.id}&tabValue=processingChecklist`;
      setTimeout(() => {
        if (tabs[i] && !tabs[i].closed) {
          tabs[i].location.href = appUrl;
        }
      }, i * 1000);
    });
  };

  return (
    <div
      className="container-fluid p-4 OrganizationLoanSearchPageContainer"
      style={{
        color: isDark ? "#e2e8f0" : "#1a202c",
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
        minHeight: "100vh",
      }}
    >
      <ToastContainer position="top-right" autoClose={3000} theme={isDark ? "dark" : "light"} />

      <div style={{ marginBottom: "28px" }}>
        <h1
          style={{
            margin: 0,
            fontSize: "24px",
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <FaFileAlt style={{ color: "#7F7FD5" }} />
          Organization Loan Search
        </h1>
        <p style={{ margin: "6px 0 0", color: "#888", fontSize: "14px" }}>
          Select an organization first, then search loans from its `loans-thin` collection.
        </p>
      </div>

      <div style={cardPanelStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "16px", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <FaDatabase style={{ color: "#888", fontSize: "13px" }} />
            <span style={{ fontSize: "13px", color: "#888" }}>Environment:</span>
            <button type="button" style={envToggleStyle("prod")} onClick={() => setEnvironment("prod")}>
              Production
            </button>
            <button type="button" style={envToggleStyle("dev")} onClick={() => setEnvironment("dev")}>
              Development
            </button>
          </div>
        </div>

        <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
          {!selectedOrganization ? (
            <>
              <div style={{ flex: 1, position: "relative", minWidth: "260px" }}>
                <FaBuilding
                  style={{
                    position: "absolute",
                    left: "14px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#888",
                    fontSize: "13px",
                    pointerEvents: "none",
                  }}
                />
                <input
                  type="text"
                  value={organizationSearchQuery}
                  onChange={(event) => setOrganizationSearchQuery(event.target.value)}
                  onKeyDown={(event) => event.key === "Enter" && runOrganizationSearch()}
                  placeholder="Search organization by name, owner ID, or organization ID"
                  style={{
                    ...inputStyle,
                    paddingLeft: "38px",
                    paddingRight: organizationSearchQuery ? "38px" : "16px",
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                />
                {organizationSearchQuery && (
                  <FaTimes
                    onClick={() => setOrganizationSearchQuery("")}
                    style={{
                      position: "absolute",
                      right: "14px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#888",
                      cursor: "pointer",
                      fontSize: "13px",
                    }}
                  />
                )}
              </div>
              {isSearchingOrganizations ? (
                <button
                  type="button"
                  onClick={() => { orgSearchRef.current = null; setIsSearchingOrganizations(false); }}
                  style={{ ...searchButtonStyle, background: "#e53e3e" }}
                >
                  <FaTimes />
                  Stop Search
                </button>
              ) : (
                <button
                  type="button"
                  onClick={runOrganizationSearch}
                  style={searchButtonStyle}
                >
                  <FaSearch />
                  {organizationSearchQuery.trim() ? "Search Orgs" : hasLoadedOrganizations ? "Show Orgs" : "Load Orgs"}
                </button>
              )}
            </>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flexWrap: "wrap",
                padding: "12px 14px",
                borderRadius: "10px",
                background: isDark ? "#1e2130" : "#f7f9fc",
                border: `1px solid ${isDark ? "#2d3148" : "#e2e8f0"}`,
                width: "100%",
              }}
            >
              <FaBuilding style={{ color: "#7F7FD5" }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700 }}>{getOrgLabel(selectedOrganization)}</div>
                <div style={{ fontSize: "12px", color: "#888" }}>{selectedOrganization.id}</div>
              </div>
              <button
                type="button"
                onClick={clearSelectedOrganization}
                style={{
                  marginLeft: "auto",
                  padding: "8px 14px",
                  borderRadius: "8px",
                  border: `1px solid ${isDark ? "#3d4466" : "#cbd5e0"}`,
                  background: "transparent",
                  color: isDark ? "#e2e8f0" : "#1a202c",
                  cursor: "pointer",
                }}
              >
                Change organization
              </button>
            </div>
          )}
        </div>

        {!selectedOrganization && isSearchingOrganizations && organizationResults.length === 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "14px", color: "#7F7FD5", fontSize: "13px" }}>
            <FaSpinner style={{ animation: "spin 1s linear infinite", flexShrink: 0 }} />
            <span>
              {orgScanProgress.total != null
                ? `${orgScanProgress.scanned.toLocaleString()} / ${orgScanProgress.total.toLocaleString()} organizations scanned…`
                : orgScanProgress.scanned > 0
                ? `${orgScanProgress.scanned.toLocaleString()} organizations scanned…`
                : "Scanning organizations…"}
            </span>
          </div>
        )}

        {!selectedOrganization && organizationResults.length > 0 && (
          <div style={{ marginTop: "18px" }}>
            <div style={{ fontSize: "12px", color: "#888", marginBottom: "8px", display: "flex", alignItems: "center", gap: "8px" }}>
              <span>
                {organizationResults.length} organization{organizationResults.length !== 1 ? "s" : ""} found
                {isSearchingOrganizations && " so far"}
                {" — select one to unlock loan search"}
              </span>
              {isSearchingOrganizations && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", color: "#7F7FD5" }}>
                  <FaSpinner style={{ animation: "spin 1s linear infinite" }} />
                  {orgScanProgress.total != null
                    ? `${orgScanProgress.scanned.toLocaleString()} / ${orgScanProgress.total.toLocaleString()} scanned`
                    : `${orgScanProgress.scanned.toLocaleString()} scanned`}
                </span>
              )}
            </div>
            <div style={{ display: "grid", gap: "10px" }}>
              {organizationResults.map((organization) => (
                <button
                  key={organization.id}
                  type="button"
                  onClick={() => selectOrganization(organization)}
                  style={{
                    textAlign: "left",
                    padding: "12px 14px",
                    borderRadius: "10px",
                    border: `1px solid ${isDark ? "#2d3148" : "#e2e8f0"}`,
                    background: isDark ? "#1e2130" : "#fff",
                    color: "inherit",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ fontWeight: 600 }}>{getOrgLabel(organization)}</div>
                  <div style={{ fontSize: "12px", color: "#888", marginTop: "3px" }}>{organization.id}</div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div style={{ ...cardPanelStyle, opacity: selectedOrganization ? 1 : 0.65 }}>
        <div style={{ marginBottom: "18px" }}>
          <h2 style={{ margin: 0, fontSize: "18px", fontWeight: 700 }}>Loan Search</h2>
          <p style={{ margin: "6px 0 0", color: "#888", fontSize: "13px" }}>
            Search is scoped to the selected organization and reads from `loans-thin`.
          </p>
        </div>

        <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ flex: 1, position: "relative", minWidth: "240px" }}>
            <FaSearch
              style={{
                position: "absolute",
                left: "14px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#888",
                fontSize: "13px",
                pointerEvents: "none",
              }}
            />
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              onKeyDown={handleLoanSearchKeyDown}
              placeholder={
                selectedOrganization
                  ? "Search by borrower, loan number, LOS ID, address, or loan officer"
                  : "Select an organization to start searching loans"
              }
              style={{
                ...inputStyle,
                paddingLeft: "38px",
                paddingRight: searchTerm ? "38px" : "16px",
                width: "100%",
                boxSizing: "border-box",
              }}
              disabled={!selectedOrganization}
            />
            {searchTerm && (
              <FaTimes
                onClick={() => setSearchTerm("")}
                style={{
                  position: "absolute",
                  right: "14px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#888",
                  cursor: "pointer",
                  fontSize: "13px",
                }}
              />
            )}
          </div>
          {isSearchingLoans ? (
            <button
              type="button"
              onClick={() => { loanListenerRef.current = null; setIsSearchingLoans(false); }}
              style={{ ...searchButtonStyle, background: "#e53e3e" }}
            >
              <FaTimes />
              Stop Search
            </button>
          ) : (
            <button
              type="button"
              onClick={runLoanSearch}
              disabled={!selectedOrganization || (!searchTerm.trim() && !stageFilter)}
              style={searchButtonStyle}
            >
              <FaSearch />
              Search Loans
            </button>
          )}
        </div>

        {selectedOrganization && stageOptions.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "14px", flexWrap: "wrap" }}>
            <span style={{ fontSize: "12px", color: "#888" }}>Stage:</span>
            <button
              type="button"
              onClick={() => setStageFilter("")}
              style={{
                padding: "4px 10px",
                fontSize: "12px",
                border: `1px solid ${stageFilter === "" ? "#7F7FD5" : isDark ? "#2d3148" : "#e2e8f0"}`,
                borderRadius: "16px",
                background: stageFilter === "" ? "rgba(127,127,213,0.12)" : "transparent",
                color: stageFilter === "" ? "#7F7FD5" : isDark ? "#a0aec0" : "#4a5568",
                cursor: "pointer",
              }}
            >
              Any stage
            </button>
            {stageOptions.map((stage) => (
              <button
                key={stage}
                type="button"
                onClick={() => setStageFilter(stage)}
                style={{
                  padding: "4px 10px",
                  fontSize: "12px",
                  border: `1px solid ${stageFilter === stage ? "#7F7FD5" : isDark ? "#2d3148" : "#e2e8f0"}`,
                  borderRadius: "16px",
                  background: stageFilter === stage ? "rgba(127,127,213,0.12)" : "transparent",
                  color: stageFilter === stage ? "#7F7FD5" : isDark ? "#a0aec0" : "#4a5568",
                  cursor: "pointer",
                }}
              >
                {stage}
              </button>
            ))}
          </div>
        )}
      </div>

      {selectedOrganization && hasSearched && (
        <div style={{ marginBottom: "20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
          <div style={{ color: "#888", fontSize: "14px" }}>
            Found <strong style={{ color: isDark ? "#e2e8f0" : "#1a202c" }}>{results.length}</strong> result
            {results.length !== 1 ? "s" : ""} for{" "}
            <strong style={{ color: "#7F7FD5" }}>"{activeSearchTerm || "stage filter"}"</strong> in{" "}
            <strong>{getOrgLabel(selectedOrganization)}</strong>
          </div>
          {results.length > 0 && (
            <button
              onClick={openAllInTabs}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "6px 14px",
                borderRadius: "8px",
                border: `1px solid ${isDark ? "#2d3148" : "#e2e8f0"}`,
                background: isDark ? "#1e2440" : "#f7f8fc",
                color: "#7F7FD5",
                fontSize: "13px",
                fontWeight: 600,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              <FaWindowRestore size={13} />
              Open all in tabs
            </button>
          )}
        </div>
      )}

      {!selectedOrganization && (
        <div style={{ textAlign: "center", padding: "48px 0", color: "#888" }}>
          <FaBuilding style={{ fontSize: "42px", opacity: 0.2, marginBottom: "14px" }} />
          <p style={{ fontSize: "16px", fontWeight: 600 }}>Select an organization to search loans</p>
          <p style={{ fontSize: "13px", opacity: 0.75, maxWidth: "460px", margin: "8px auto 0" }}>
            This tool is organization-scoped by design, so the organization choice is required before any loan query runs.
          </p>
        </div>
      )}

      {selectedOrganization && !isSearchingLoans && hasSearched && results.length === 0 && (
        <div style={{ textAlign: "center", padding: "48px 0", color: "#888" }}>
          <FaSearch style={{ fontSize: "42px", opacity: 0.2, marginBottom: "14px" }} />
          <p style={{ fontSize: "16px", fontWeight: 600 }}>No matching loans found</p>
          <p style={{ fontSize: "13px", opacity: 0.75, maxWidth: "460px", margin: "8px auto 0" }}>
            Try a shorter query, a different borrower or address fragment, or adjust the selected stage.
          </p>
        </div>
      )}

      {isSearchingLoans && results.length === 0 && (
        <div style={{ textAlign: "center", padding: "48px 0", color: "#7F7FD5" }}>
          <FaSpinner style={{ fontSize: "32px", animation: "spin 1s linear infinite" }} />
          <p style={{ marginTop: "12px", color: "#888" }}>
            {scanProgress.total != null
              ? `${scanProgress.scanned.toLocaleString()} / ${scanProgress.total.toLocaleString()} records scanned…`
              : scanProgress.scanned > 0
              ? `${scanProgress.scanned.toLocaleString()} records scanned…`
              : "Scanning loans…"}
          </p>
        </div>
      )}

      {isSearchingLoans && results.length > 0 && (
        <div style={{ marginBottom: "12px", display: "flex", alignItems: "center", gap: "8px", color: "#7F7FD5", fontSize: "13px" }}>
          <FaSpinner style={{ animation: "spin 1s linear infinite", flexShrink: 0 }} />
          <span>
            {scanProgress.total != null
              ? `${scanProgress.scanned.toLocaleString()} / ${scanProgress.total.toLocaleString()} records scanned…`
              : `${scanProgress.scanned.toLocaleString()} records scanned…`}
          </span>
        </div>
      )}

      {results.length > 0 && (
        <div style={{ display: "grid", gap: "12px" }}>
          {results.map((loan) => {
            const borrowerName = getBorrowerName(loan);
            const propertyAddress = getPropertyAddress(loan);
            const stage = getLoanStage(loan);
            const loanNumber = getLoanNumber(loan);
            const loanType = getLoanType(loan);
            const updatedAt = formatUpdatedAt(loan);
            const appUrl = `https://app.addy.so/loans?tab=board&loan=${loan.id}&tabValue=processingChecklist`;

            return (
              <div
                key={loan.id}
                style={{
                  background: isDark ? "#161b2e" : "#fff",
                  border: `1px solid ${isDark ? "#2d3148" : "#e2e8f0"}`,
                  borderRadius: "12px",
                  padding: "18px 20px",
                  boxShadow: isDark ? "0 4px 24px rgba(0,0,0,0.22)" : "0 2px 12px rgba(0,0,0,0.04)",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                      <div style={{ fontWeight: 700, fontSize: "16px" }}>
                        <HighlightText text={getLoanLabel(loan)} term={activeSearchTerm} />
                      </div>
                      {stage && (
                        <span
                          style={{
                            fontSize: "11px",
                            padding: "2px 8px",
                            borderRadius: "12px",
                            background: "rgba(127,127,213,0.14)",
                            color: "#7F7FD5",
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {stage}
                        </span>
                      )}
                    </div>
                    <div style={{ marginTop: "6px", color: "#888", fontSize: "13px" }}>
                      <HighlightText text={borrowerName || propertyAddress || "No borrower or address available"} term={activeSearchTerm} />
                    </div>
                  </div>
                  <a
                    href={appUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      color: "#7F7FD5",
                      fontSize: "13px",
                      fontWeight: 600,
                      textDecoration: "none",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Open in app
                    <FaExternalLinkAlt size={11} />
                  </a>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: "12px",
                    marginTop: "16px",
                    fontSize: "13px",
                  }}
                >
                  <div>
                    <div style={{ color: "#888", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "4px" }}>
                      Loan ID
                    </div>
                    <div style={{ fontFamily: "monospace", fontSize: "12px", wordBreak: "break-all" }}>{loan.id}</div>
                  </div>
                  <div>
                    <div style={{ color: "#888", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "4px" }}>
                      Loan Number
                    </div>
                    <div><HighlightText text={loanNumber} term={activeSearchTerm} /></div>
                  </div>
                  <div>
                    <div style={{ color: "#888", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "4px" }}>
                      Borrower
                    </div>
                    <div><HighlightText text={borrowerName} term={activeSearchTerm} /></div>
                  </div>
                  <div>
                    <div style={{ color: "#888", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "4px" }}>
                      Property
                    </div>
                    <div><HighlightText text={propertyAddress} term={activeSearchTerm} /></div>
                  </div>
                  <div>
                    <div style={{ color: "#888", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "4px" }}>
                      Loan Officer
                    </div>
                    <div>
                      <HighlightText
                        text={loan.authorName || loan.loanOfficerName || loan.authorEmail || loan.loanOfficerEmail}
                        term={activeSearchTerm}
                      />
                    </div>
                  </div>
                  {loanType && (
                    <div>
                      <div style={{ color: "#888", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "4px" }}>
                        Loan Type
                      </div>
                      <div>
                        <HighlightText text={loanType} term={activeSearchTerm} />
                      </div>
                    </div>
                  )}
                  {updatedAt && (
                    <div>
                      <div style={{ color: "#888", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "4px" }}>
                        Updated At
                      </div>
                      <div>{updatedAt}</div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .OrganizationLoanSearchPageContainer [style*="cursor: pointer"]:hover { opacity: 0.88; }
      `}</style>
    </div>
  );
};

export default OrganizationLoanSearchPage;
